import {useFormik} from 'formik';
import {useSchemes} from '../../../shared';
import {CreateFundDTO, FundDetailedModel, useFunds} from '../../../entities';
import * as Yup from 'yup';
import {AxiosError} from 'axios';
import {useNavigate, useParams} from 'react-router';

type UseEditFundFormProps = {
  navigateAfterSuccess?: boolean;
};

export const useEditFundForm = ({navigateAfterSuccess = true}: UseEditFundFormProps = {}) => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {
    nameSchema,
    shortDescriptionSchema,
    detailedDescriptionSchema,
    emailNotRequiredSchema,
    phoneNumberNotRequiredSchema,
    websiteSchema,
  } = useSchemes();

  const {getFundById, updateFundMutation} = useFunds({
    query: id,
  });

  const {data: fundData, isLoading: isFundLoading} = getFundById;
  const fund = fundData as FundDetailedModel | null;

  const onSubmit = async (values: CreateFundDTO) => {
    try {
      if (id) {
        await updateFundMutation.mutateAsync({
          fundId: id,
          data: values,
        });
        if (navigateAfterSuccess) {
          navigate(`/fundraisings/funds/${id}`);
        }
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: fund?.name || '',
      shortDescription: fund?.shortDescription || '',
      detailedDescription: fund?.detailedDescription || '',
      phoneNumber: fund?.phoneNumber || '',
      email: fund?.email || '',
      website: fund?.website || '',
      avatar: undefined,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: nameSchema,
      shortDescription: shortDescriptionSchema,
      detailedDescription: detailedDescriptionSchema,
      email: emailNotRequiredSchema,
      phoneNumber: phoneNumberNotRequiredSchema,
      website: websiteSchema,
    }),
    onSubmit,
  });

  return {
    ...formik,
    isLoading: updateFundMutation.isPending || isFundLoading,
    submitError: updateFundMutation.error
      ? (updateFundMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    submitForm: () => formik.handleSubmit(),
  };
};
